import React from "react"
import { MultiSelectListItem } from "./MultiSelectListItem"
import { flattenData, isItemExpanded } from "../utils"
import { MultiSelectItem, MultiSelectSearchMode, MultiSelectSelectMode } from "../MultiSelect"

type Props<TValue> = {
    selectedIds: TValue[]
    setSelectedIds: (selectedIds: TValue[]) => void
    disableOnly?: boolean
    expanded: Record<string, boolean>
    onToggleExpand: (itemId: TValue) => void
    filteredItems: MultiSelectItem<TValue>[]
    searchValue: string
    selectMode: MultiSelectSelectMode
    searchMode: MultiSelectSearchMode
    emptyMessage: string
    collapseNested: boolean
    onItemClickedOverride?: (
        item: MultiSelectItem<TValue>,
        isSelected: boolean,
        selectedIds: TValue[],
        onlyClicked: boolean
    ) => TValue[]
}
export const MultiSelectItemsList = <TValue extends string>({
    filteredItems,
    selectedIds,
    collapseNested,
    searchValue,
    expanded,
    selectMode,
    disableOnly,
    setSelectedIds,
    onToggleExpand,
    onItemClickedOverride,
    emptyMessage,
}: Props<TValue>) => {
    const onItemClicked = (itemId: TValue, item: MultiSelectItem<TValue>) => {
        if (selectMode === MultiSelectSelectMode.ItemsOnly && item.children?.length) {
            // With 'ItemsOnly' selection mode, if we click a parent item, we want to select/deselect all children
            const childrenItems = flattenData(item.children).filter(child => !child.children)
            const isChecked = childrenItems.every(child => selectedIds.includes(child.id))

            if (isChecked) {
                // When unchecking the parent item, we want to uncheck all children
                setSelectedIds(selectedIds.filter(id => !childrenItems.some(child => child.id === id)))
                return
            }
            // When checking the parent item, we want to check all children
            setSelectedIds([
                ...selectedIds,
                ...childrenItems.filter(child => !selectedIds.includes(child.id)).map(child => child.id),
            ])
            return
        }

        const isSelected = !selectedIds.includes(itemId)

        // If there is a custom logic for getting the new selectedIds, use it
        if (onItemClickedOverride) {
            // todo: revisit whether old agendas still need the 'onItemClickedOverride' prop
            // as we introduced the 'ItemsOnly' selection mode
            setSelectedIds(onItemClickedOverride(item, isSelected, selectedIds, false))
            return
        }

        // Default logic: just check/uncheck clicked item id
        if (selectedIds.includes(itemId)) {
            setSelectedIds(selectedIds.filter(id => id !== itemId))
        } else {
            setSelectedIds([...selectedIds, itemId])
        }
    }

    const onOnlyClicked = (itemId: TValue, item: MultiSelectItem<TValue>) => {
        if (item.children) {
            if (selectMode === MultiSelectSelectMode.ItemsOnly) {
                // With 'ItemsOnly' selection mode, if we click "Only" on a parent item,
                // we want to select all children, but not the parent item itself
                const childrenItems = flattenData(item.children).filter(child => !child.children)
                setSelectedIds([...childrenItems.map(child => child.id)])
                return
            }

            setSelectedIds([itemId, ...item.children.map(child => child.id)])
        } else {
            // If there is a custom logic for getting the new selectedIds, use it
            if (onItemClickedOverride) {
                setSelectedIds(onItemClickedOverride(item, true, selectedIds, true))
                return
            }
            setSelectedIds([itemId])
        }
    }

    const getItemCheckboxState = (
        item: MultiSelectItem<TValue>
    ): {
        isChecked: boolean
        isIndeterminate: boolean
        isExpanded: boolean
    } => {
        const isExpanded = isItemExpanded(expanded, item.id, !!collapseNested, searchValue)

        if (selectMode === MultiSelectSelectMode.ItemsOnly && item.children?.length) {
            // With 'ItemsOnly' selection mode, if an item has children, check if all children are selected or not.
            // Checkbox state of such an item depends on the children selection state
            const allChildren = flattenData(item.children).filter(child => !child.children)
            const checked = allChildren.every(child => selectedIds.includes(child.id))
            const isSomeChecked = allChildren.some(child => selectedIds.includes(child.id))

            return {
                isChecked: checked,
                isIndeterminate: isSomeChecked && !checked,
                isExpanded: isExpanded,
            }
        }

        return {
            isChecked: selectedIds.includes(item.id),
            isIndeterminate: false,
            isExpanded: isExpanded,
        }
    }

    const renderItems = (items: MultiSelectItem<TValue>[], level: number) => {
        return items.map(item => {
            const checkedItems = selectedIds
            const isDisableOnly =
                // Do not show "Only" button, if the 'disableOnly' prop is set,
                // or if the item is already selected.
                disableOnly || (checkedItems.length === 1 && checkedItems[0] === item.id)

            const { isChecked, isIndeterminate, isExpanded } = getItemCheckboxState(item)
            return (
                <React.Fragment key={item.id}>
                    <MultiSelectListItem
                        isChecked={isChecked}
                        isIndeterminate={isIndeterminate}
                        item={item}
                        onClick={onItemClicked}
                        onOnlyClick={onOnlyClicked}
                        disableOnly={isDisableOnly}
                        labelOnly={item.labelOnly}
                        searchLabel={searchValue}
                        isCollapsible={!!(collapseNested && item.children?.length)}
                        hasCollapsibleItems={collapseNested}
                        isExpanded={isExpanded}
                        onToggleExpand={onToggleExpand}
                        level={level}
                    />
                    {item.children && isExpanded ? <div>{renderItems(item.children, level + 1)}</div> : null}
                </React.Fragment>
            )
        })
    }

    const renderEmptyMessage = () => {
        return <div className={classes.emptyMsg}>{emptyMessage}</div>
    }

    const renderContent = () => {
        if (filteredItems.length > 0) {
            return renderItems(filteredItems, 0)
        } else {
            return renderEmptyMessage()
        }
    }

    return <>{renderContent()}</>
}

const classes = {
    emptyMsg: "py-3 text-center text-black/50",
}
